import type {
  EntityApi,
  EntityFilters,
  EntityListParams,
  EntityState,
  IdentifiableEntity,
} from '@/shared/types/entity';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

type EntityStateMap<T extends IdentifiableEntity, F extends EntityFilters> = Record<
  string,
  EntityState<T, F>
>;

export interface CreateEntitySliceConfig<T extends IdentifiableEntity, F extends EntityFilters, D> {
  name: string;
  api: EntityApi<T, D>;
  initialState: EntityState<T, F>;
  getListParams?: (state: EntityStateMap<T, F>) => EntityListParams;
}

export const createEntitySlice = <T extends IdentifiableEntity, F extends EntityFilters, D>({
  name,
  api,
  initialState,
  getListParams,
}: CreateEntitySliceConfig<T, F, D>) => {
  // Async thunks
  const fetchItems = createAsyncThunk(`${name}/fetchItems`, async (_, { getState }) => {
    const state = getState() as EntityStateMap<T, F>;
    const entityState = state[name];
    const params = getListParams
      ? getListParams(state)
      : {
          page: entityState.currentPage,
          perPage: entityState.perPage,
          ...entityState.filters,
        };
    return await api.getList(params);
  });

  const createItem = createAsyncThunk(`${name}/createItem`, async (data: D) => {
    return await api.create(data);
  });

  const updateItem = createAsyncThunk(
    `${name}/updateItem`,
    async ({ id, data }: { id: number; data: Partial<D> }) => {
      return await api.update(id, data);
    },
  );

  const deleteItem = createAsyncThunk(`${name}/deleteItem`, async (id: number) => {
    await api.delete(id);
    return id;
  });

  // Slice
  const slice = createSlice({
    name,
    initialState,
    reducers: {
      setPage: (state, action: PayloadAction<number>) => {
        state.currentPage = action.payload;
      },
      setPerPage: (state, action: PayloadAction<number>) => {
        state.perPage = action.payload;
        state.currentPage = 1;
      },
      setFilters: (state, action: PayloadAction<Partial<F>>) => {
        state.filters = { ...state.filters, ...action.payload };
        state.currentPage = 1;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchItems.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchItems.fulfilled, (state, action) => {
          const { data, currentPage, perPage, total, lastPage } = action.payload;
          state.loading = false;
          // Ensure data is always an array
          state.items = (Array.isArray(data) ? data : []) as Draft<T>[];
          state.currentPage = currentPage;
          if (perPage !== undefined) {
            state.perPage = perPage;
          }
          state.total = total;
          state.lastPage = lastPage;
        })
        .addCase(fetchItems.rejected, (state, action) => {
          state.loading = false;
          // Ensure items is always an array
          if (!state.items) {
            state.items = [];
          }
          // Preserve the original error with status code for proper handling
          if (action.payload && typeof action.payload === 'object' && 'status' in action.payload) {
            state.error = action.payload as EntityState<T, F>['error'];
          } else {
            state.error = action.error.message || `Failed to fetch ${name}`;
          }
        })
        .addCase(createItem.fulfilled, (state, action: PayloadAction<T>) => {
          state.items.push(action.payload as Draft<T>);
        })
        .addCase(updateItem.fulfilled, (state, action: PayloadAction<T>) => {
          const index = state.items.findIndex((item) => item.id === action.payload.id);
          if (index !== -1) {
            state.items[index] = action.payload as Draft<T>;
          }
        })
        .addCase(deleteItem.pending, (state) => {
          state.error = null;
        })
        .addCase(deleteItem.fulfilled, (state, action) => {
          state.items = state.items.filter((item) => item.id !== action.payload);
          const nextTotal = Math.max(0, state.total - 1);
          state.total = nextTotal;
          const perPage = state.perPage > 0 ? state.perPage : 1;
          const newLastPage = Math.max(1, Math.ceil(nextTotal / perPage));
          state.lastPage = newLastPage;
          if (state.currentPage > newLastPage) {
            state.currentPage = newLastPage;
          }
        })
        .addCase(deleteItem.rejected, (state, action) => {
          // Preserve the original error with status code for proper handling
          if (action.payload && typeof action.payload === 'object' && 'status' in action.payload) {
            state.error = action.payload as EntityState<T, F>['error'];
          } else {
            state.error = action.error.message || `Failed to delete ${name}`;
          }
        });
    },
  });

  return {
    slice,
    actions: {
      fetchItems,
      createItem,
      updateItem,
      deleteItem,
      setPage: slice.actions.setPage,
      setPerPage: slice.actions.setPerPage,
      setFilters: slice.actions.setFilters,
    },
    reducer: slice.reducer,
  };
};
